import { EventEmitter, Injectable } from '@angular/core';
import { TimelineCanvasLevel } from '../timeline/timeline-canvas/timeline-canvas-level';
import { RecorderStore } from '../stores/recorder.store';
import { RecordingConfig } from '../interfaces/recording-config';
import { SCRIPT_PROCESSOR_BUFFER_SIZE } from '../../constants';
import { MessageService } from '../../shared/services/message.service';
import { WebAudioFactory } from '../factories/web-audio.factory';
import { Deferred } from '../../shared/helpers/deferred';

let instance: Recorder;

@Injectable()
export class Recorder {
  private scriptProcessorBufferSize = SCRIPT_PROCESSOR_BUFFER_SIZE;
  private audioContext: AudioContext;
  private scriptProcessor: ScriptProcessorNode;
  private mediaStream: MediaStream;
  private mediaStreamAudioSource: MediaStreamAudioSourceNode;

  public recording = false;
  public recordingChange: EventEmitter<boolean> = new EventEmitter();

  onProgress: EventEmitter<TimelineCanvasLevel[]> = new EventEmitter();

  public config: RecordingConfig;

  constructor(
    private recorderStore: RecorderStore,
    private messageService: MessageService,
    private webAudioFactory: WebAudioFactory,
  ) {
    if (instance) {
      return instance;
    } else {
      instance = this;
    }

    this.onAudioProcess = this.onAudioProcess.bind(this);
  }

  private setRecording(value: boolean) {
    this.recording = value;
    this.recordingChange.emit(value);
  }

  stopRecording() {
    this.stopCollectAudioData();
  }

  startRecording(config: RecordingConfig) {
    this.config = config;

    if (this.hasInfrastructureToBePrepared()) {
      this.prepareInfrastructure()
        .then(
          () => this.preStartCollectAudioData(),
          error => this.messageService.showError(error),
        );
    } else {
      this.preStartCollectAudioData();
    }
  }

  private hasInfrastructureToBePrepared(): boolean {
    return this.mediaStreamAudioSource === undefined;
  }

  private prepareInfrastructure(): Promise<any> {
    return this.requestStream()
      .then(() => this.createContext())
      .then(() => this.createScriptProcessor())
      .then(() => this.createMediaStreamAudioSource());
  }

  private createContext(): Promise<any> {
    this.audioContext = this.webAudioFactory.getAudioContext();

    if (this.audioContext) {
      return Promise.resolve();
    } else {
      return Promise.reject(this.messageService.browserError);
    }
  }

  private createScriptProcessor(): Promise<any> {
    if (this.audioContext.createScriptProcessor) {
      this.scriptProcessor = this.audioContext.createScriptProcessor(this.scriptProcessorBufferSize, 1, 1);

      return Promise.resolve();
    } else {
      return Promise.reject(this.messageService.browserError);
    }
  }

  private createMediaStreamAudioSource(): Promise<any> {
    this.mediaStreamAudioSource = this.audioContext.createMediaStreamSource(this.mediaStream);
    return Promise.resolve();
  }

  private requestStream(): Promise<any> {
    navigator.getUserMedia = navigator.getUserMedia ||
      navigator['webkitGetUserMedia'] ||
      navigator['mozGetUserMedia'] ||
      navigator['msGetUserMedia'];

    if (!navigator.getUserMedia) {
      return Promise.reject(this.messageService.browserError);
    }

    const deferred = new Deferred;

    navigator.getUserMedia(
      {audio: true},
      mediaStream => deferred.resolve(this.mediaStream = mediaStream),
      error => deferred.reject(this.processRequestStreamError(error)),
    );

    return deferred.promise;
  }

  private processRequestStreamError(error: Error): Error {
    switch (error.name) {
      default:
        return error;
    }
  }

  private preStartCollectAudioData() {
    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume()
        .then(() => this.startCollectAudioData());
    } else {
      this.startCollectAudioData();
    }
  }

  private startCollectAudioData() {
    this.mediaStreamAudioSource.connect(this.scriptProcessor);
    this.scriptProcessor.connect(this.audioContext.destination);
    this.scriptProcessor.addEventListener('audioprocess', this.onAudioProcess);

    this.recorderStore.startRecording(this.config);

    this.setRecording(true);
  }

  private stopCollectAudioData() {
    this.mediaStreamAudioSource.disconnect(this.scriptProcessor);
    this.scriptProcessor.disconnect(this.audioContext.destination);
    this.scriptProcessor.removeEventListener('audioprocess', this.onAudioProcess);

    this.recorderStore.stopRecording();

    this.setRecording(false);
  }

  private onAudioProcess(event: AudioProcessingEvent) {
    this.recorderStore.addRecordingProgress(event.inputBuffer.getChannelData(0))
      .then(levels => this.onProgress.emit(levels));
  }
}



// WEBPACK FOOTER //
// ./src/app/rec/entities/recorder.ts